// Catalog.ID Client — Changing a device's unlock mode (DOM-free)
// ============================================================================
// device-setup-restructure §15, decided 2026-09-18. The rule, in one line:
//
//     PRESENT EVERY FACTOR YOU REMOVE OR CHANGE.
//
// `PUT /api/device/pin` is step-up gated, and the step-up proves custody of
// `main_signing_key` — which every legitimate caller has, because it is sitting
// decrypted in memory. What it does NOT prove is that the member is standing
// there. So a change that takes a factor AWAY has to present that factor: the
// current PIN to remove or replace the PIN, a live code to remove the
// Authenticator factor. Adding one is unchanged — a live code when an
// Authenticator mode is chosen, nothing at all when a PIN is added.
//
// Two things this module is NOT:
//
//  * It is not the gate. The node's `factors_required` answer is the gate, and
//    it is authoritative: {@link factorsToPresent} exists only so the member is
//    asked ONCE, before the write, instead of being refused and then asked.
//    Every caller repaints from {@link classifyModeChangeFailure} when the node
//    disagrees.
//  * It is not DOM. The inputs live in `components/factor-inputs.ts`, shared
//    with the unlock screen, and the flow that drives them in
//    `components/device-mode-confirm.ts` — the project's pattern since
//    `authenticator.ts`.

import { ApiError, isSessionRejected } from '../api/client';
import { describeAuthenticatorError } from './authenticator';
import type { UnlockFactor } from './device-unlock';

/** What the device row asks for TODAY. `pinLength` is this browser's local
 *  record (the device list does not carry it), so it may be absent. */
export interface DeviceModeNow {
    accessMode: string;
    totpRequired: boolean;
    pinLength?: number | null;
}

/** What the member has just chosen — the fields of the `changePin` write. */
export interface DeviceModeNext {
    accessMode: string;
    totpRequired: boolean;
    /** A NEW PIN, when one was typed. Its presence is what makes this a PIN
     *  change; its absence with the mode unchanged is "keep the PIN" (§6.5). */
    pin?: string;
    pinLength?: number;
}

/**
 * Which of the device's CURRENT factors this change has to present. Mirrors
 * `DeviceController.ChangeRemovesOrReplacesThePin` plus `req.TotpRequired ||
 * device.TotpRequired`, in the node's order — `pin` first, because the node
 * verifies it first and outside the code limiter.
 *
 * The PIN clause has three ways in and one deliberate way out:
 *  * the mode drops the PIN,
 *  * a new PIN is supplied (its value changes — the node cannot tell "the same
 *    PIN again" from a replacement without being shown the old one),
 *  * its length changes.
 * The way out is "keep the PIN": the mode is unchanged, no `pin` is supplied
 * and no differing length is — which is exactly the Login code step, holding
 * the member's phone and not their PIN.
 *
 * An UNKNOWN current length cannot tell a length change from a keep, so it
 * claims nothing and lets the node answer; in practice the mode-change modal
 * always carries a freshly typed PIN for a PIN mode, which triggers the clause
 * above it anyway.
 */
export function factorsToPresent(current: DeviceModeNow, next: DeviceModeNext): UnlockFactor[] {
    const factors: UnlockFactor[] = [];
    if (current.accessMode === 'pin' && removesOrReplacesThePin(current, next)) factors.push('pin');
    if (next.totpRequired || current.totpRequired) factors.push('totp');
    return factors;
}

function removesOrReplacesThePin(current: DeviceModeNow, next: DeviceModeNext): boolean {
    if (next.accessMode !== 'pin') return true;
    if (next.pin !== undefined) return true;
    if (next.pinLength === undefined) return false;
    if (current.pinLength === undefined || current.pinLength === null) return false;
    return next.pinLength !== current.pinLength;
}

/**
 * The device's current mode, read back out of the factors it asks to unlock
 * (§6.1). The supersecret re-key screen never lists devices, so this local
 * hint is the only record of the row it has — and, like every use of the hint,
 * it is an opening guess that the node's `factors_required` answer overrides.
 */
export function modeFromUnlockFactors(
    factors: readonly UnlockFactor[], pinLength?: number | null,
): DeviceModeNow {
    const pin = factors.includes('pin');
    return {
        accessMode: pin ? 'pin' : 'open',
        totpRequired: factors.includes('totp'),
        ...(pin ? { pinLength } : {}),
    };
}

/**
 * Of the factors this change must present, the ones not already in hand.
 *
 * At most ONE code is ever needed per change — the node's `code` field carries
 * the live code that proves the member holds the key AND the code being removed
 * — so when the mode step has already taken one, the confirm step must not ask
 * for a second. The PIN is never in hand before the confirm step, but it is
 * after a refusal that kept it, which is why this takes both.
 */
export function factorsStillToCollect(
    needed: readonly UnlockFactor[], held: { currentPin?: string; code?: string },
): UnlockFactor[] {
    return needed.filter(f =>
        f === 'pin' ? held.currentPin === undefined : held.code === undefined);
}

// ── refusals ────────────────────────────────────────────────────────────────

/** The node's two verbatim refusals on this endpoint. Unlike `unlock`, a bad
 *  code here is a 400, not a 401 — the write endpoints answer through
 *  `RequireAuthenticatorChoiceAsync`. */
const WRONG_PIN = 'Wrong PIN';
const CODE_FAILURE = 'Invalid authenticator code';

export type ModeChangeOutcome =
    | { status: 'factors-required'; factors: UnlockFactor[] }
    | { status: 'wrong-pin'; message: string }
    | { status: 'wrong-code'; message: string }
    | { status: 'rate-limited'; message: string; retryAfterSeconds: number | null }
    | { status: 'locked'; message: string }
    | { status: 'device-unknown'; message: string }
    | { status: 'session-rejected'; message: string }
    | { status: 'failed'; message: string };

function serverError(err: ApiError): string | null {
    const body = err.body as { error?: unknown } | null;
    return typeof body?.error === 'string' ? body.error : null;
}

/** The factors named by a `400 { error: "factors_required", factors: [...] }`.
 *  Strict, for the same reason as on `unlock`: a list carrying a factor this
 *  client cannot render would loop the member on a screen that can never
 *  satisfy the node, so it is read as "not a factors refusal" instead. */
function requiredFactorsFrom(err: unknown): UnlockFactor[] | null {
    if (!(err instanceof ApiError) || err.status !== 400) return null;
    const body = err.body as { error?: unknown; factors?: unknown } | null;
    if (!body || body.error !== 'factors_required' || !Array.isArray(body.factors)) return null;
    if (body.factors.length === 0) return null;
    if (!body.factors.every(f => f === 'pin' || f === 'totp')) return null;
    return body.factors as UnlockFactor[];
}

/**
 * Classify anything `changePin` can throw, so the screen has one vocabulary.
 * Never throws.
 */
export function classifyModeChangeFailure(err: unknown): ModeChangeOutcome {
    const factors = requiredFactorsFrom(err);
    if (factors) return { status: 'factors-required', factors };

    if (isSessionRejected(err)) {
        return {
            status: 'session-rejected',
            message: 'Your session has ended — sign in with your password again.',
        };
    }
    if (err instanceof ApiError) {
        if (err.status === 423) {
            return {
                status: 'locked',
                message: 'Too many wrong PINs — this device is now locked. Sign out and unlock it '
                    + 'with your supersecret to choose a new PIN.',
            };
        }
        if (err.status === 404) {
            return {
                status: 'device-unknown',
                message: 'This device is no longer registered — sign out and set it up again.',
            };
        }
        if (err.status === 429) {
            const body = err.body as { retryAfterSeconds?: unknown } | null;
            return {
                status: 'rate-limited',
                message: describeAuthenticatorError(err),
                retryAfterSeconds: typeof body?.retryAfterSeconds === 'number' ? body.retryAfterSeconds : null,
            };
        }
        const text = serverError(err);
        if (text === WRONG_PIN) return { status: 'wrong-pin', message: 'Wrong PIN. Try again.' };
        if (text === CODE_FAILURE) {
            return { status: 'wrong-code', message: describeAuthenticatorError(err) };
        }
    }
    return {
        status: 'failed',
        message: err instanceof Error && err.message ? err.message : 'Could not change this device',
    };
}

/** One line per outcome, for the modal's error area. */
export function describeModeChangeError(outcome: ModeChangeOutcome): string {
    return outcome.status === 'factors-required'
        ? 'Confirm with the factors this device still asks for.'
        : outcome.message;
}

/** True when no amount of re-typing helps: the member has to leave this screen.
 *  A locked device needs the supersecret, and so does a row that has vanished —
 *  both are reached by signing out, which is where the caller sends them. */
export function routesOutOfTheModal(outcome: ModeChangeOutcome): boolean {
    return outcome.status === 'locked'
        || outcome.status === 'device-unknown'
        || outcome.status === 'session-rejected'
        || outcome.status === 'failed';
}
